/**
 * DAAM Leads — Owner settings page (Phase P0, client-only).
 *
 * Mobile-first, Arabic-RTL by default. All layout is a single column at
 * <768px and a two-column split (form + live preview) at ≥lg.
 *
 * P0 responsibilities:
 *   • Read /api/leads/mine → either editable state, or the "create page"
 *     bootstrap with fallback business_name/logo from client_profiles /
 *     brand_settings.
 *   • Slug availability probing (debounced) via /api/leads/slug-check.
 *   • Save changes via POST /api/leads (first save) or PATCH /api/leads/mine.
 *   • Logo upload via POST /api/leads/mine/logo (magic-byte validated
 *     server-side; UI enforces size + type up-front for a fast reject).
 *   • Per-button enable switches + is_published master switch.
 *   • A live preview card that mirrors the public /l/:slug view without
 *     leaking any payload we wouldn't otherwise expose.
 *
 * NOT in this file: analytics, tracking, campaigns, redirects.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import { Card, CardContent, CardHeader, CardTitle }
  from "@/components/ui/card";
import { Button }   from "@/components/ui/button";
import { Input }    from "@/components/ui/input";
import { Label }    from "@/components/ui/label";
import { Switch }   from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge }    from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast }    from "sonner";
import { Link } from "react-router-dom";
import {
  Link2, Loader2, Upload, Check, X, Phone, MessageCircle,
  ShoppingBag, Globe2, Copy, Eye, EyeOff, Megaphone, Plus,
  BarChart3,
} from "lucide-react";
import { api } from "@/lib/api";
import LeadsPaywallBanner from "@/components/leads/LeadsPaywallBanner";

const PUBLIC_ORIGIN = window.location.origin;
const SLUG_HINT_REGEX = /^[a-z0-9](?:[a-z0-9]|-(?=[a-z0-9])){1,38}[a-z0-9]$/;

// Client-side pre-filter — matches server contract. Server is the
// authoritative validator; this only fails fast for obvious mistakes.
const LOGO_MAX_BYTES  = 1 * 1024 * 1024;
const LOGO_MIME_LIST  = ["image/png", "image/jpeg", "image/webp"];

// Closed source allow-list — must mirror backend LEAD_SOURCES.
const CAMPAIGN_SOURCES = [
  { key: "instagram", label: "Instagram" },
  { key: "tiktok",    label: "TikTok"    },
  { key: "snapchat",  label: "Snapchat"  },
  { key: "x",         label: "X (Twitter)" },
  { key: "direct",    label: "مباشر"      },
  { key: "other",     label: "أخرى"       },
];
const CAMPAIGN_CODE_HINT = /^[a-z0-9-]{2,40}$/;

// ----- helpers --------------------------------------------------------------
function humanErr(e) {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "خطأ غير متوقع";
  return d.message_ar || d.message_en || d.code || String(d);
}

// ============================================================================
// Public preview card — mirrors PublicLeadPage's visible surface. Reads
// straight from the local state so it updates as the owner types.
// ============================================================================
function LivePreview({ page }) {
  const wa = page.whatsapp_number, cn = page.call_number;
  const buttons = [
    { key: "whatsapp", icon: MessageCircle, label: "واتساب",
      enabled: page.buttons_enabled?.whatsapp && Boolean(wa) },
    { key: "call",     icon: Phone,         label: "اتصال",
      enabled: page.buttons_enabled?.call     && Boolean(cn) },
    { key: "order",    icon: ShoppingBag,   label: "اطلب الآن",
      enabled: page.buttons_enabled?.order    && Boolean(page.order_url) },
    { key: "website",  icon: Globe2,        label: "الموقع",
      enabled: page.buttons_enabled?.website  && Boolean(page.website_url) },
  ];
  return (
    <div dir="rtl"
         data-testid="leads-live-preview"
         className="rounded-3xl border border-neutral-200 bg-white shadow-sm overflow-hidden">
      <div className="p-6 sm:p-7 text-center bg-gradient-to-b from-neutral-50 to-white">
        <div className="mx-auto h-20 w-20 rounded-2xl overflow-hidden bg-neutral-100 flex items-center justify-center">
          {page.logo_url ? (
            <img src={page.logo_url} alt=""
                 className="w-full h-full object-cover" />
          ) : (
            <span className="text-2xl font-semibold text-neutral-400">
              {(page.business_name || "؟").trim().charAt(0)}
            </span>
          )}
        </div>
        <h3 className="mt-4 text-lg font-semibold text-neutral-900">
          {page.business_name || "اسم النشاط"}
        </h3>
        {page.short_description ? (
          <p className="mt-1 text-sm text-neutral-600 leading-6">
            {page.short_description}
          </p>
        ) : null}
      </div>
      <div className="px-4 pb-6 space-y-2.5">
        {buttons.map((b) => (
          <div key={b.key}
               data-testid={`leads-preview-btn-${b.key}`}
               className={`w-full rounded-2xl px-4 py-3.5 text-sm font-medium flex items-center justify-center gap-2 border ${
                 b.enabled
                   ? "border-neutral-900 bg-neutral-900 text-white"
                   : "border-neutral-200 bg-neutral-50 text-neutral-400"}`}>
            <b.icon className="h-4 w-4" />
            <span>{b.label}</span>
            {!b.enabled && (
              <span className="text-[10px] font-normal opacity-70 mr-1">
                (معطّل)
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Campaigns section — client-only. Uses /api/leads/campaigns.
// Terminology guardrail: never labels a click as a "customer" or a
// "confirmed order". Approved copy: "مشاهدات الصفحة", "نقرات واتساب",
// "نقرات الاتصال", "نقرات الطلب", "زوار اتخذوا إجراءً — تقديري".
// ============================================================================
function CampaignsSection({ page, exists, entitlements }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows]       = useState([]);
  const [name, setName]       = useState("");
  const [code, setCode]       = useState("");
  const [src,  setSrc]        = useState("instagram");
  const [saving, setSaving]   = useState(false);

  // Per-plan cap surfaced by the backend. `null` (no data or older
  // backend) → no limit shown, no upgrade hint. `null` INSIDE
  // entitlements means "unlimited" — Business.
  const capRaw    = entitlements?.max_active_campaigns;
  const cap       = (capRaw === null || capRaw === undefined) ? null : Number(capRaw);
  const tier      = entitlements?.tier || null;
  const activeCnt = useMemo(() => rows.filter((r) => r.is_active).length, [rows]);
  const atCap     = cap != null && activeCnt >= cap;

  const load = useCallback(async () => {
    if (!exists) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/leads/campaigns");
      setRows(data.campaigns || []);
    } catch { /* silent — campaign panel is optional */ }
    finally { setLoading(false); }
  }, [exists]);
  useEffect(() => { load(); }, [load]);

  const codeSanitised = code.trim().toLowerCase();
  const codeValid = CAMPAIGN_CODE_HINT.test(codeSanitised);
  const canCreate = name.trim() && codeValid && !saving;

  async function createOne() {
    if (!canCreate) return;
    setSaving(true);
    try {
      await api.post("/leads/campaigns", {
        name: name.trim(), code: codeSanitised, source: src,
        is_active: true,
      });
      toast.success("تم إنشاء رابط الحملة");
      setName(""); setCode(""); setSrc("instagram");
      await load();
    } catch (e) { toast.error(humanErr(e)); }
    finally    { setSaving(false); }
  }

  async function toggleActive(row) {
    try {
      const { data } = await api.patch(`/leads/campaigns/${row.id}`,
                                        { is_active: !row.is_active });
      setRows((old) => old.map((r) => r.id === data.id ? data : r));
    } catch (e) { toast.error(humanErr(e)); }
  }

  function copyVisitUrl(row) {
    // Prefer the OG-aware share URL when the campaign has been promoted
    // to a landing page (slug exists). Falls back to the legacy UTM-only
    // visit URL for pre-landing-page campaigns.
    const base = window.location.origin;
    const businessSlug = page?.slug?.trim()?.toLowerCase() || "";
    const campSlug = (row.slug || "").trim().toLowerCase();
    const full = (businessSlug && campSlug)
      ? `${base}/api/leads/share/${businessSlug}/${campSlug}`
      : `${base}${row.visit_url}`;
    navigator.clipboard.writeText(full).then(
      () => toast.success("تم نسخ رابط الحملة"),
      () => toast.error("تعذّر النسخ"),
    );
  }

  if (!exists) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Megaphone className="h-4 w-4" /> روابط الحملات
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-neutral-500">
            أنشئ الصفحة أولًا لتتمكني من إضافة روابط الحملات.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="leads-campaigns-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Megaphone className="h-4 w-4" /> روابط الحملات
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* per-plan cap indicator */}
        {cap !== null && (
          <div className="flex items-center justify-between rounded-xl
                          bg-neutral-50 border border-neutral-200 px-3.5 py-2 text-xs"
               data-testid="leads-campaigns-cap">
            <div className="text-neutral-600">
              الحملات النشطة{" "}
              <span className={`font-semibold ${atCap ? "text-amber-700" : "text-neutral-900"}`}>
                {activeCnt} / {cap}
              </span>
              {tier && (
                <span className="ms-2 text-[11px] text-neutral-400">
                  · باقة {tier === "creator" ? "صانع محتوى" :
                            tier === "pro"     ? "احترافي"     :
                            tier === "business"? "أعمال"       :
                            tier === "trial"   ? "التجربة"     : tier}
                </span>
              )}
            </div>
            {atCap && (
              <a href="/app/plans"
                 data-testid="leads-campaigns-upgrade-cta"
                 className="text-amber-800 underline underline-offset-4 hover:text-amber-900">
                ترقية الباقة
              </a>
            )}
          </div>
        )}

        {/* create form */}
        <div className="rounded-2xl border border-dashed border-neutral-200 p-3.5 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">اسم الحملة</Label>
              <Input value={name}
                     data-testid="leads-campaign-name-input"
                     onChange={(e) => setName(e.target.value.slice(0, 80))}
                     placeholder="مثال: قصة رمضان 2026"
                     className="mt-1 text-sm" />
            </div>
            <div>
              <Label className="text-xs">كود الحملة</Label>
              <Input value={code}
                     dir="ltr"
                     data-testid="leads-campaign-code-input"
                     onChange={(e) =>
                       setCode(e.target.value.toLowerCase()
                                .replace(/[^a-z0-9-]/g, ""))}
                     placeholder="ramadan26"
                     className="mt-1 text-sm" />
              {code && !codeValid && (
                <p className="text-xs text-red-500 mt-1">
                  2–40 حرفًا صغيرة/أرقام/شرطات
                </p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
            <div>
              <Label className="text-xs">المنصة</Label>
              <select value={src}
                      onChange={(e) => setSrc(e.target.value)}
                      data-testid="leads-campaign-source-select"
                      className="mt-1 w-full h-10 rounded-md border border-neutral-200 px-3 text-sm bg-white">
                {CAMPAIGN_SOURCES.map((s) => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
              </select>
            </div>
            <Button onClick={createOne} disabled={!canCreate}
                    data-testid="leads-campaign-create-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" />
                      : (<><Plus className="h-4 w-4 me-1.5" /> إضافة</>)}
            </Button>
          </div>
        </div>

        {/* list */}
        {loading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-neutral-500 text-center py-4">
            لا توجد حملات بعد. أضيفي رابط حملتك الأولى أعلاه.
          </p>
        ) : (
          <ul className="divide-y divide-neutral-100 rounded-2xl border border-neutral-200 overflow-hidden">
            {rows.map((r) => (
              <li key={r.id}
                  data-testid={`leads-campaign-row-${r.code}`}
                  className={`p-3.5 flex flex-col sm:flex-row sm:items-center gap-3 ${
                    r.is_active ? "" : "bg-neutral-50 opacity-70"}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    {r.name}
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px]">
                    <Badge variant="outline" className="font-mono">
                      {r.code}
                    </Badge>
                    <Badge variant="outline">
                      {CAMPAIGN_SOURCES.find((s) => s.key === r.source)?.label
                        || r.source}
                    </Badge>
                    {!r.is_active && (
                      <Badge className="bg-neutral-200 text-neutral-700 hover:bg-neutral-200">
                        معطّلة
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 justify-end">
                  <Link to={`/app/leads/campaigns/${r.id}/edit`}
                        data-testid={`leads-campaign-edit-${r.code}`}>
                    <Button size="sm" variant="outline">
                      <Megaphone className="h-3.5 w-3.5 me-1.5" /> تعديل الصفحة
                    </Button>
                  </Link>
                  <Button size="sm" variant="outline"
                          onClick={() => copyVisitUrl(r)}
                          data-testid={`leads-campaign-copy-${r.code}`}>
                    <Copy className="h-3.5 w-3.5 me-1.5" /> نسخ
                  </Button>
                  <Switch checked={r.is_active}
                          onCheckedChange={() => toggleActive(r)}
                          data-testid={`leads-campaign-active-${r.code}`} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}


// ============================================================================
// Main page
// ============================================================================
const EMPTY = {
  slug: "", business_name: "", short_description: "",
  whatsapp_number: "", call_number: "",
  order_url: "", website_url: "", logo_url: null,
  buttons_enabled: { whatsapp: true, call: true, order: true, website: true },
  is_published: false,
};

export default function LeadsPage() {
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [uploading, setUploading] = useState(false);
  const [exists,  setExists]  = useState(false);
  const [page,    setPage]    = useState(EMPTY);
  const [slugStatus, setSlugStatus] = useState(null); // {available, reason}
  // Gate state populated when the API rejects with 402 / 403 due to
  // the DAAM Leads subscription gate. Rendered as an inline paywall
  // banner instead of the normal editor UI.
  const [gate,    setGate]    = useState(null); // { code, message_ar, message_en }
  // Per-plan entitlements from GET /leads/mine (tier, allowed ranges,
  // max active campaigns). Passed to <CampaignsSection> so it can
  // display the correct cap + upgrade hint.
  const [entitlements, setEntitlements] = useState(null);
  // Original slug after the last successful save — compared against
  // `page.slug` to detect pending slug changes that require the
  // destructive-change confirmation dialog.
  const [savedSlug, setSavedSlug] = useState("");
  const [slugConfirm, setSlugConfirm] = useState(null); // {oldSlug, newSlug} | null
  const slugCheckTimer = useRef(null);
  const fileInputRef   = useRef(null);

  // ------- initial load ----------------------------------------------------
  const load = useCallback(async () => {
    setLoading(true);
    setGate(null);
    try {
      const { data } = await api.get("/leads/mine");
      // Cache per-plan entitlements returned alongside the page shape so
      // the CampaignsSection can render its cap hint without a second
      // round-trip. Missing / older backends → leave null (all UI
      // features remain available).
      setEntitlements(data.entitlements || null);
      if (data.exists) {
        setExists(true);
        setSavedSlug(data.slug || "");
        setPage({
          slug: data.slug || "",
          business_name: data.business_name || "",
          short_description: data.short_description || "",
          whatsapp_number: data.whatsapp_number || "",
          call_number: data.call_number || "",
          order_url: data.order_url || "",
          website_url: data.website_url || "",
          logo_url: data.logo_url || null,
          buttons_enabled: data.buttons_enabled || EMPTY.buttons_enabled,
          is_published: !!data.is_published,
        });
      } else {
        setExists(false);
        setSavedSlug("");
        const fb = data.fallback_defaults || {};
        setPage((p) => ({ ...p,
          business_name: fb.business_name || "",
          logo_url:      fb.logo_url      || null,
        }));
      }
    } catch (e) {
      // Gate rejections have a documented body shape. Render the
      // paywall banner instead of the raw error toast (which the
      // interceptor already surfaced).
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      if ((status === 402 || status === 403) && detail?.code) {
        setGate(detail);
      } else {
        toast.error(humanErr(e));
      }
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // ------- slug availability — debounced on every slug typing ------------
  // Runs for both initial creation (!exists) and post-save slug edits
  // (exists && slug !== savedSlug). When the slug equals the saved
  // value we clear status so no stale indicator lingers.
  useEffect(() => {
    const s = page.slug.trim().toLowerCase();
    if (!s) { setSlugStatus(null); return; }
    if (exists && s === savedSlug) { setSlugStatus(null); return; }
    clearTimeout(slugCheckTimer.current);
    slugCheckTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/leads/slug-check", { params: { slug: s } });
        setSlugStatus(data);
      } catch { setSlugStatus(null); }
    }, 350);
    return () => clearTimeout(slugCheckTimer.current);
  }, [page.slug, exists, savedSlug]);

  // ------- field setters ----------------------------------------------------
  const setField = (k, v) => setPage((p) => ({ ...p, [k]: v }));
  const setButton = (k, v) => setPage((p) => ({
    ...p, buttons_enabled: { ...p.buttons_enabled, [k]: v },
  }));

  // ------- save ------------------------------------------------------------
  // Public API: `requestSave()` is the handler bound to the Save button;
  // it diverts into the destructive slug-change confirmation when
  // appropriate. All other paths call `performSave()` directly.
  function requestSave() {
    const newSlug = (page.slug || "").trim().toLowerCase();
    if (exists && savedSlug && newSlug && newSlug !== savedSlug) {
      setSlugConfirm({ oldSlug: savedSlug, newSlug });
      return;
    }
    performSave();
  }
  function confirmSlugChange() {
    setSlugConfirm(null);
    performSave();
  }

  async function performSave() {
    setSaving(true);
    try {
      if (!exists) {
        // Client-side sanity checks — server is the authoritative validator
        // but a fast local reject avoids a round-trip.
        if (!SLUG_HINT_REGEX.test(page.slug.trim().toLowerCase())) {
          toast.error("الرابط المختصر غير صالح. استخدم 3–40 حرفًا صغيرة أو أرقامًا أو شرطات."); return;
        }
        if (!page.business_name.trim()) {
          toast.error("اسم النشاط مطلوب."); return;
        }
        const body = { ...page, slug: page.slug.trim().toLowerCase(),
                       whatsapp_number: page.whatsapp_number || null,
                       call_number:     page.call_number     || null,
                       order_url:       page.order_url       || null,
                       website_url:     page.website_url     || null };
        const { data } = await api.post("/leads", body);
        setExists(true);
        setSavedSlug(data.slug || body.slug);
        setPage((p) => ({ ...p, ...data }));
        toast.success("تم إنشاء صفحة العميل بنجاح");
      } else {
        // PATCH the allowlisted subset. `slug` is included only when it
        // actually changed; the backend accepts it post-creation but
        // uniqueness is enforced server-side.
        const newSlug = (page.slug || "").trim().toLowerCase();
        const slugChanged = savedSlug && newSlug && newSlug !== savedSlug;
        const body = {
          business_name:     page.business_name,
          short_description: page.short_description,
          whatsapp_number:   page.whatsapp_number || null,
          call_number:       page.call_number     || null,
          order_url:         page.order_url       || null,
          website_url:       page.website_url     || null,
          buttons_enabled:   page.buttons_enabled,
          is_published:      page.is_published,
          ...(slugChanged ? { slug: newSlug } : {}),
        };
        const { data } = await api.patch("/leads/mine", body);
        if (data?.slug) setSavedSlug(data.slug);
        setPage((p) => ({ ...p, ...data }));
        toast.success(slugChanged
          ? "تم حفظ التغييرات. الرابط القديم لم يعد يعمل."
          : "تم حفظ التغييرات");
      }
    } catch (e) { toast.error(humanErr(e)); }
    finally    { setSaving(false); }
  }

  // ------- logo upload ------------------------------------------------------
  async function handleLogoPick(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    // fast reject — server still re-validates via magic bytes.
    if (!LOGO_MIME_LIST.includes(f.type)) {
      toast.error("نوع الملف غير مدعوم. الرجاء رفع PNG أو JPEG أو WebP.");
      e.target.value = "";
      return;
    }
    if (f.size > LOGO_MAX_BYTES) {
      toast.error("الملف أكبر من 1 ميغابايت.");
      e.target.value = "";
      return;
    }
    if (!exists) {
      toast.error("احفظ الصفحة أولًا قبل رفع الشعار.");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/leads/mine/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPage((p) => ({ ...p, logo_url: data.logo_url }));
      toast.success("تم رفع الشعار");
    } catch (er) { toast.error(humanErr(er)); }
    finally      { setUploading(false); e.target.value = ""; }
  }

  // ------- misc UI helpers --------------------------------------------------
  const publicUrl = useMemo(() =>
    page.slug ? `${PUBLIC_ORIGIN}/l/${page.slug.trim().toLowerCase()}` : "",
    [page.slug]);
  const copyPublicUrl = () => {
    if (!publicUrl) return;
    navigator.clipboard.writeText(publicUrl).then(
      () => toast.success("تم نسخ الرابط"),
      () => toast.error("تعذّر نسخ الرابط"),
    );
  };

  if (loading) {
    return (
      <AppShell>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-neutral-500" />
        </div>
      </AppShell>
    );
  }

  // Subscription gate short-circuit — render the on-page paywall
  // instead of the editor. `EMAIL_VERIFICATION_REQUIRED` (403) is
  // still forwarded to /verify-email by the api.js interceptor.
  if (gate && gate.code !== "EMAIL_VERIFICATION_REQUIRED") {
    return (
      <AppShell>
        <div dir="rtl" data-testid="leads-page-paywall"
             className="mx-auto max-w-3xl px-4 sm:px-6 py-8 sm:py-12">
          <header className="mb-6">
            <h1 className="text-2xl sm:text-3xl font-semibold text-neutral-900">
              DAAM Leads
            </h1>
          </header>
          <LeadsPaywallBanner
            title={gate.message_ar || "فعّل اشتراكك لاستخدام DAAM Leads"}
            message="أنشئ صفحة نشاطك، قِس الزيارات والنقرات، واعرف أي منصة تحقق نتائج أفضل."
            ctaTo={gate.action?.route || "/app/plans"} />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div dir="rtl" data-testid="leads-page"
           className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-8 space-y-6">
        {/* header */}
        <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-neutral-900">
              DAAM Leads
            </h1>
            <p className="mt-1 text-sm text-neutral-600 leading-6">
              حوّل زيارات حسابات التواصل إلى نقرات واتساب واتصالات وطلبات قابلة للقياس.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant={page.is_published ? "default" : "secondary"}
                   data-testid="leads-status-badge"
                   className={page.is_published
                     ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100"
                     : ""}>
              {page.is_published ? "منشورة" : "مسودة"}
            </Badge>
            <Link to="/app/leads/analytics"
                  data-testid="leads-view-analytics-link">
              <Button variant="outline" size="sm">
                <BarChart3 className="h-4 w-4 me-1.5" /> عرض النتائج
              </Button>
            </Link>
            <Button onClick={requestSave} disabled={saving}
                    data-testid="leads-save-btn"
                    className="min-w-[140px]">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" />
                      : (exists ? "حفظ التغييرات" : "إنشاء الصفحة")}
            </Button>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-6">
          {/* ------- FORM COLUMN -------------------------------------------- */}
          <div className="space-y-6">
            {/* Slug + link */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Link2 className="h-4 w-4" /> رابط الصفحة العامة
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Label htmlFor="slug" className="text-sm">
                  الرابط المختصر
                </Label>
                <div className="flex items-stretch gap-2">
                  <div className="flex-1 relative">
                    <Input id="slug" dir="ltr"
                           data-testid="leads-slug-input"
                           value={page.slug}
                           onChange={(e) => setField("slug",
                             e.target.value.toLowerCase()
                               .replace(/[^a-z0-9-]/g, ""))}
                           placeholder="nexora-cafe"
                           className="pr-9" />
                    {page.slug && slugStatus && (
                      <span className="absolute inset-y-0 left-2.5 flex items-center"
                            data-testid="leads-slug-status">
                        {slugStatus.available
                          ? <Check className="h-4 w-4 text-emerald-500" />
                          : <X     className="h-4 w-4 text-red-500" />}
                      </span>
                    )}
                  </div>
                  <Button type="button" variant="outline" size="icon"
                          disabled={!publicUrl}
                          onClick={copyPublicUrl}
                          data-testid="leads-copy-link-btn"
                          title="نسخ الرابط">
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-neutral-500 leading-5 break-all">
                  {publicUrl ? `العنوان الكامل: ${publicUrl}` :
                   "3–40 حرفًا صغيرة أو أرقامًا أو شرطات."}
                </p>
                {exists && savedSlug && page.slug && page.slug !== savedSlug && (
                  <div data-testid="leads-business-slug-warning"
                       className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] leading-6 text-amber-900">
                    <b>تحذير:</b> تغيير رابط النشاط من
                    <span className="mx-1 inline-block font-mono bg-white/70 rounded px-1.5 py-0.5">{savedSlug}</span>
                    إلى
                    <span className="mx-1 inline-block font-mono bg-white/70 rounded px-1.5 py-0.5">{page.slug}</span>
                    سيوقف فورًا كل رابط DAAM Leads سبق أن شاركته —
                    بما في ذلك جميع صفحات الحملات التابعة لهذا النشاط.
                    حملاتك وبياناتها وتحليلاتها ستُحفظ كما هي، ويتغيّر
                    عنوان الروابط فقط.
                  </div>
                )}
                {!exists && page.slug && slugStatus && !slugStatus.available && (
                  <p className="text-xs text-red-600" data-testid="leads-slug-error">
                    {slugStatus.reason === "reserved_slug"
                      ? "هذا الرابط محجوز، اختر رابطًا آخر."
                      : slugStatus.reason === "invalid_slug"
                      ? "صيغة الرابط غير صحيحة."
                      : "الرابط مأخوذ من مستخدم آخر."}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Branding */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">هوية النشاط</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-sm">اسم النشاط</Label>
                  <Input value={page.business_name}
                         data-testid="leads-name-input"
                         onChange={(e) => setField("business_name",
                                                    e.target.value.slice(0, 80))}
                         placeholder="مثال: Nexora Café"
                         className="mt-1" />
                </div>
                <div>
                  <Label className="text-sm">وصف مختصر</Label>
                  <Textarea rows={3}
                            data-testid="leads-desc-input"
                            value={page.short_description}
                            onChange={(e) => setField("short_description",
                                                     e.target.value.slice(0, 200))}
                            placeholder="جملة تعريفية قصيرة يراها زوار الصفحة"
                            className="mt-1 resize-none" />
                  <div className="text-xs text-neutral-400 mt-1">
                    {page.short_description.length}/200
                  </div>
                </div>
                <Separator />
                <div>
                  <Label className="text-sm">الشعار</Label>
                  <div className="mt-2 flex items-center gap-3">
                    <div className="h-14 w-14 rounded-xl bg-neutral-100 overflow-hidden flex items-center justify-center">
                      {page.logo_url
                        ? <img src={page.logo_url} alt=""
                               className="w-full h-full object-cover" />
                        : <span className="text-lg text-neutral-400">
                            {(page.business_name || "؟").trim().charAt(0)}
                          </span>}
                    </div>
                    <div>
                      <input ref={fileInputRef}
                             type="file"
                             accept="image/png,image/jpeg,image/webp"
                             onChange={handleLogoPick}
                             className="hidden"
                             data-testid="leads-logo-file-input" />
                      <Button type="button" variant="outline"
                              disabled={uploading || !exists}
                              onClick={() => fileInputRef.current?.click()}
                              data-testid="leads-logo-upload-btn">
                        {uploading
                          ? <Loader2 className="h-4 w-4 animate-spin" />
                          : (<><Upload className="h-4 w-4 me-2" /> رفع شعار</>)}
                      </Button>
                      <p className="text-xs text-neutral-500 mt-1.5 leading-5">
                        PNG أو JPEG أو WebP فقط، بحجم أقصى 1 ميغابايت.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact + buttons */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">أزرار الصفحة</CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                {/* whatsapp */}
                <ContactRow
                  testid="whatsapp" icon={MessageCircle} label="زر واتساب"
                  hint="بصيغة دولية تبدأ بـ + مثل +9665…"
                  value={page.whatsapp_number}
                  onChange={(v) => setField("whatsapp_number", v)}
                  enabled={page.buttons_enabled.whatsapp}
                  onEnabledChange={(v) => setButton("whatsapp", v)} />
                {/* call */}
                <ContactRow
                  testid="call" icon={Phone} label="زر الاتصال"
                  hint="نفس الصيغة الدولية"
                  value={page.call_number}
                  onChange={(v) => setField("call_number", v)}
                  enabled={page.buttons_enabled.call}
                  onEnabledChange={(v) => setButton("call", v)} />
                {/* order */}
                <ContactRow
                  testid="order" icon={ShoppingBag} label="زر الطلب / المتجر"
                  hint="رابط https:// إلى صفحة الطلب"
                  value={page.order_url}
                  onChange={(v) => setField("order_url", v)}
                  enabled={page.buttons_enabled.order}
                  onEnabledChange={(v) => setButton("order", v)}
                  ltr />
                {/* website */}
                <ContactRow
                  testid="website" icon={Globe2} label="زر الموقع"
                  hint="رابط https:// إلى الموقع الرسمي"
                  value={page.website_url}
                  onChange={(v) => setField("website_url", v)}
                  enabled={page.buttons_enabled.website}
                  onEnabledChange={(v) => setButton("website", v)}
                  ltr />
              </CardContent>
            </Card>

            {/* Publish */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">النشر</CardTitle>
              </CardHeader>
              <CardContent className="flex items-center gap-3">
                {page.is_published
                  ? <Eye     className="h-5 w-5 text-emerald-600" />
                  : <EyeOff  className="h-5 w-5 text-neutral-400" />}
                <div className="flex-1">
                  <div className="text-sm font-medium">
                    {page.is_published
                      ? "الصفحة منشورة ومتاحة للعملاء"
                      : "الصفحة مسودة — لن تظهر لأي أحد"}
                  </div>
                  <div className="text-xs text-neutral-500 mt-0.5">
                    يمكنك التبديل بين النشر والمسودة في أي وقت.
                  </div>
                </div>
                <Switch checked={page.is_published}
                        onCheckedChange={(v) => setField("is_published", v)}
                        data-testid="leads-publish-switch" />
              </CardContent>
            </Card>

            {/* Campaigns — client-only */}
            <CampaignsSection page={page} exists={exists}
                              entitlements={entitlements} />
          </div>

          {/* ------- PREVIEW COLUMN ------------------------------------------ */}
          <div className="lg:sticky lg:top-6 self-start">
            <div className="mb-3 text-xs text-neutral-500">معاينة الصفحة</div>
            <LivePreview page={page} />
          </div>
        </div>
      </div>

      {/* -------- Business-slug destructive-change confirmation -------- */}
      <AlertDialog open={!!slugConfirm} onOpenChange={(o) => !o && setSlugConfirm(null)}>
        <AlertDialogContent dir="rtl" data-testid="leads-business-slug-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-start">
              تأكيد تغيير رابط النشاط
            </AlertDialogTitle>
            <AlertDialogDescription className="text-start leading-7">
              سيتغيّر رابط صفحتك من
              <span className="mx-1 inline-block font-mono bg-neutral-100 rounded px-1.5 py-0.5">
                /l/{slugConfirm?.oldSlug}
              </span>
              إلى
              <span className="mx-1 inline-block font-mono bg-amber-50 border border-amber-300 rounded px-1.5 py-0.5">
                /l/{slugConfirm?.newSlug}
              </span>
              . بعد الحفظ:
              <ul className="list-disc ps-5 mt-2 space-y-1 text-[13px]">
                <li>كل رابط DAAM Leads سبق أن شاركته لصفحة النشاط
                    وجميع صفحات حملاتك ستفتح «غير متاحة» (404).</li>
                <li><b>تُحفظ</b> جميع حملاتك ووسائطها وتحليلاتها
                    السابقة كما هي — عنوان الروابط فقط يتغيّر.</li>
                <li>لا توجد إعادة توجيه تلقائية من الرابط القديم للجديد.</li>
                <li>الرابط القديم «{slugConfirm?.oldSlug}» سيتحرر فورًا
                    ويمكن لعميل آخر حجزه.</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row-reverse gap-2">
            <AlertDialogAction onClick={confirmSlugChange}
                               data-testid="leads-business-slug-confirm-ok"
                               className="bg-amber-600 hover:bg-amber-700">
              نعم، غيّر الرابط
            </AlertDialogAction>
            <AlertDialogCancel data-testid="leads-business-slug-confirm-cancel">
              إلغاء
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}

// ============================================================================
// One contact-row control — label + enable switch + input + hint.
// ============================================================================
function ContactRow({ testid, icon: Icon, label, hint, value, onChange,
                      enabled, onEnabledChange, ltr = false }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <Label className="text-sm flex items-center gap-2">
          <Icon className="h-4 w-4 text-neutral-500" /> {label}
        </Label>
        <Switch checked={enabled}
                onCheckedChange={onEnabledChange}
                data-testid={`leads-${testid}-switch`} />
      </div>
      <Input value={value || ""}
             dir={ltr ? "ltr" : "ltr"}
             disabled={!enabled}
             onChange={(e) => onChange(e.target.value)}
             data-testid={`leads-${testid}-input`}
             className="text-sm" />
      <p className="text-xs text-neutral-500 mt-1">{hint}</p>
    </div>
  );
}
